import { HStack, Spinner, Stack, Table } from "@chakra-ui/react"
import { Text } from "@/components"
import { CashFlow, PaymentRecord } from "@/api/payments"

interface Props {
  cashFlows: CashFlow[]
  paymentHistory?: PaymentRecord[]
  isLoading: boolean
}

const statusColors: Record<string, string> = {
  PENDING: "yellow.600",
  COMPLETED: "yellow.600",
  RELEASED: "green.700",
  FAILED: "red.600",
  CANCELLED: "gray.500",
  // A released payment that came back (payments server, 2026-10-12). It no
  // longer counts toward "released"; the client also gets an email and their
  // equity partner follows up on the bank details.
  RETURNED: "orange.600",
}

const statusLabels: Record<string, string> = {
  PENDING: "Pending Release",
  COMPLETED: "Pending Release",
  RELEASED: "Released",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
  RETURNED: "Returned",
}

export const PaymentLedgerSection = ({ cashFlows, paymentHistory = [], isLoading }: Props) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(Math.abs(amount))
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    } catch {
      return dateStr
    }
  }

  // Pick the most relevant date for a payment row
  const getPaymentDate = (p: PaymentRecord) => {
    if (p.status === "RELEASED" && p.released_at) return formatDate(p.released_at)
    if ((p.status === "COMPLETED") && p.completed_at) return formatDate(p.completed_at)
    if (p.approved_at) return formatDate(p.approved_at)
    return formatDate(p.created_at)
  }

  // Build a description from available fields
  // Format: "Description (Wire via Bank A)" — combines VBA description with transfer details
  const getPaymentDescription = (p: PaymentRecord) => {
    // Build the transfer detail part: "(Wire via Bank A)"
    const transferParts: string[] = []
    if (p.transfer_type) transferParts.push(p.transfer_type)
    if (p.bank_name) transferParts.push(`via ${p.bank_name}`)
    const transferDetail = transferParts.length > 0 ? `(${transferParts.join(" ")})` : ""

    // If there's a memo (description from VBA), show it first, then transfer details
    if (p.memo) {
      return transferDetail ? `${p.memo} ${transferDetail}` : p.memo
    }

    // No memo — just show transfer details without parens
    return transferParts.length > 0 ? transferParts.join(" ") : "\u2014"
  }

  // The pipeline steps an in-flight payment has actually passed, from the
  // timestamps the API already returns. PENDING covers both pending_approval and
  // approved server-side; approved_at is what tells them apart here. No step is
  // claimed without its timestamp, and no delivery-time promises are made.
  const shortDate = (dateStr?: string | null) => {
    if (!dateStr) return null
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    } catch {
      return null
    }
  }
  const pipelineSteps = (p: PaymentRecord): { label: string; date: string | null; done: boolean }[] => [
    { label: "Submitted", date: shortDate(p.created_at), done: true },
    { label: "Approved", date: shortDate(p.approved_at), done: !!p.approved_at },
    { label: "Sent to bank", date: shortDate(p.completed_at), done: !!p.completed_at },
    { label: "Released", date: shortDate(p.released_at), done: !!p.released_at },
  ]
  const isInFlight = (p: PaymentRecord) => p.status === "PENDING" || p.status === "COMPLETED"

  const hasData = cashFlows.length > 0 || paymentHistory.length > 0

  return (
    <Stack>
      <Text fontSize="lg" fontWeight="semibold">
        Payment History
      </Text>

      {isLoading ? (
        <HStack w="100%" justifyContent="center" p={6}>
          <Spinner />
        </HStack>
      ) : !hasData ? (
        <Text color="gray.500" p={4}>
          No payment history yet.
        </Text>
      ) : (
        <Table.Root size="sm" mt={2}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Date</Table.ColumnHeader>
              <Table.ColumnHeader>Description</Table.ColumnHeader>
              <Table.ColumnHeader>Amount</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paymentHistory.map((p, index) => (
              <Table.Row key={`ph-${p.id ?? index}`}>
                <Table.Cell>{getPaymentDate(p)}</Table.Cell>
                <Table.Cell>{getPaymentDescription(p)}</Table.Cell>
                <Table.Cell color={p.status === "RELEASED" ? "green.600" : statusColors[p.status] || "gray.600"}>{formatCurrency(p.amount)}</Table.Cell>
                <Table.Cell color={statusColors[p.status] || "gray.600"}>
                  {isInFlight(p) ? (
                    <Stack gap={0}>
                      {pipelineSteps(p).map((st) => (
                        <Text key={st.label} fontSize="xs" color={st.done ? "green.700" : "gray.400"}>
                          {st.done ? "\u2713" : "\u25CB"} {st.label}{st.done && st.date ? ` \u00B7 ${st.date}` : ""}
                        </Text>
                      ))}
                    </Stack>
                  ) : (
                    statusLabels[p.status] || p.status
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
            {cashFlows.map((cf, index) => (
              <Table.Row key={`cf-${cf.id ?? index}`}>
                <Table.Cell>{formatDate(cf.date)}</Table.Cell>
                <Table.Cell>{cf.memo || "Cash flow"}</Table.Cell>
                <Table.Cell color={cf.amount < 0 ? "red.600" : "green.600"}>{formatCurrency(cf.amount)}</Table.Cell>
                <Table.Cell color="green.600">Recorded</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Stack>
  )
}
